import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Calendar,
  Users,
  UserCircle,
  BarChart3,
  Settings,
  FolderOpen,
  LogOut,
  Menu,
  X,
  UsersRound,
  FileText
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const navItems = [
  { path: '/weeks', label: 'Недели', mobileLabel: 'Недели', icon: Calendar },
  { path: '/leads', label: 'Лиды', mobileLabel: 'Лиды', icon: Users },
  { path: '/daily-reports', label: 'Дневной отчёт', mobileLabel: 'Отчёт', icon: FileText },
  { path: '/teams', label: 'Команды', mobileLabel: 'Команды', icon: UsersRound, adminOnly: true },
  { path: '/managers', label: 'Менеджеры', mobileLabel: 'Менеджеры', icon: UserCircle, adminOnly: true },
  { path: '/categories', label: 'Категории', mobileLabel: 'Категории', icon: FolderOpen, adminOnly: true },
  { path: '/reports', label: 'Фин. отчёты', mobileLabel: 'Отчёты', icon: BarChart3 },
  { path: '/settings', label: 'Настройки', mobileLabel: 'Ещё', icon: Settings },
];

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isTeamLeader } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const filteredNavItems = navItems.filter(
    (item) => {
      if (isAdmin) return true;
      // Team leader and manager: hide admin-only items
      return !item.adminOnly;
    }
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname.startsWith(item.path);
    const Icon = item.icon;

    return (
      <Link
        to={item.path}
        onClick={() => mobile && setMobileOpen(false)}
        className={cn(
          'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200',
          isActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        )}
        data-testid={`nav-${item.path.replace('/', '')}`}
      >
        <Icon className="h-5 w-5" />
        <span>{item.label}</span>
      </Link>
    );
  };

  // Desktop Sidebar
  const Sidebar = () => (
    <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 flex-col border-r bg-[hsl(var(--nav-background))] z-40">
      <div className="flex items-center gap-3 p-6 border-b">
        <div className="w-10 h-10 rounded-lg overflow-hidden">
          <img src="/logo.png" alt="Logo" className="w-full h-full object-cover" />
        </div>
        <div>
          <h1 className="font-semibold text-lg">Walter White Team</h1>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {filteredNavItems.map((item) => (
          <NavLink key={item.path} item={item} />
        ))}
      </nav>

      <div className="p-4 border-t">
        <div className="flex items-center gap-3 px-4 py-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <UserCircle className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.username}</p>
            <p className="text-xs text-muted-foreground">
              {user?.role === 'admin' ? 'Администратор' : user?.role === 'team_leader' ? 'Тимлид' : 'Менеджер'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-destructive"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Выйти
        </Button>
      </div>
    </aside>
  );

  // Mobile Bottom Navigation
  const MobileNav = () => {
    const mainItems = filteredNavItems.slice(0, 4);
    const hasMore = filteredNavItems.length > 4;

    return (
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[hsl(var(--nav-background))] border-t z-50 safe-area-pb">
        <div className="flex items-center justify-around py-2">
          {mainItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            const Icon = item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'nav-item min-w-0',
                  isActive ? 'nav-item-active' : 'nav-item-inactive'
                )}
                data-testid={`mobile-nav-${item.path.replace('/', '')}`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className="text-[10px] truncate max-w-[60px]">{item.mobileLabel || item.label}</span>
              </Link>
            );
          })}

          {hasMore && (
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  className={cn(
                    'nav-item',
                    mobileOpen ? 'nav-item-active' : 'nav-item-inactive'
                  )}
                  data-testid="mobile-nav-more"
                >
                  {mobileOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                  <span className="text-xs">Ещё</span>
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-auto pb-20">
                <div className="space-y-2 pt-4">
                  {filteredNavItems.slice(4).map((item) => (
                    <NavLink key={item.path} item={item} mobile />
                  ))}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg w-full text-left text-destructive hover:bg-destructive/10 transition-colors"
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut className="h-5 w-5" />
                    <span>Выйти</span>
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </nav>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <MobileNav />
      <main className="page-container">
        <div className="page-content animate-fade-in">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
